import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, connect } from "react-redux";
import {
  TextField as MuiTextField,
  Snackbar,
} from "@material-ui/core";
import { Alert as SnackbarAlert } from "@material-ui/lab";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Alert,
  Modal as ReactstrapModal,
  ModalHeader,
  ModalBody,
} from "reactstrap";

import {
  getResourceGroups,
  addResourceGroup,
  editResourceGroup,
  deleteResourceGroup,
  clearAllResourceGroupMessages,
  getListOfOrgsAndAccts,
} from "../../store/actions";
import accessToken from "../../helpers/jwt-token-access/accessToken";
import jwtDecode from "jwt-decode";
import spinner from "../../assets/images/aws/loadingGif.gif";
import getUserRole from "../../helpers/jwt-token-access/userRole";
import { ACCOUNT_DETAIL_FONT_SX } from "../../components/AccountDetailPolicy/accountDetailTypography";
import {
  EditAccountSortableHeader,
  EditAccountTablePagination,
} from "../../components/AccountDetailPolicy/editAccountTableControls";
import {
  AccountDetailTooltip,
  TruncatedTooltipText,
} from "../../components/AccountDetailPolicy/editAccountTooltipStyles";

const allowedRoles = ["Administrator"];

const BRAND_ORANGE = "#F98125";
const BRAND_ORANGE_HOVER = "#e8741c";

const CELL_TEXT = {
  fontSize: "0.8125rem",
  fontWeight: 500,
  color: "#334155",
  lineHeight: 1.4,
};

const HEADER_CELL_SX = {
  ...CELL_TEXT,
  fontWeight: 600,
  fontSize: "0.6875rem",
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: "#64748b",
  py: 1.5,
  px: 1.75,
  borderBottom: "1px solid #e2e8f0",
  backgroundColor: "#fff",
  whiteSpace: "nowrap",
};

const BODY_CELL_SX = {
  ...CELL_TEXT,
  py: 1.5,
  px: 1.75,
  borderBottom: "1px solid #f1f5f9",
  verticalAlign: "middle",
  whiteSpace: "nowrap",
  overflow: "hidden",
};

const DELETE_BTN_SX = {
  width: 30,
  height: 30,
  borderRadius: "8px",
  border: "1px solid #fecaca",
  backgroundColor: "#fef2f2",
  color: "#dc2626",
  padding: 0,
  "&:hover": {
    backgroundColor: "#fee2e2",
    color: "#991b1b",
  },
  "&.Mui-disabled": {
    color: "#fca5a5",
    borderColor: "#fee2e2",
    backgroundColor: "#fff",
  },
};

const GREY_CLOSE_BTN_SX = {
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.875rem",
  borderRadius: "8px",
  px: 2.5,
  py: 0.85,
  color: "#fff",
  backgroundColor: "#6c757d",
  border: "1px solid #6c757d",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "#5a6268",
    borderColor: "#5a6268",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    backgroundColor: "#adb5bd",
    borderColor: "#adb5bd",
    color: "#fff",
  },
};

const CLOSE_X_STYLE = {
  padding: 0,
  margin: 0,
  border: "none",
  background: "transparent",
  color: "#FFFFFF",
  fontSize: 34,
  fontWeight: 900,
  lineHeight: 0.85,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  boxShadow: "none",
  flexShrink: 0,
  WebkitTextStroke: "0.6px #FFFFFF",
};

const PRIMARY_BTN_SX = {
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.875rem",
  borderRadius: "8px",
  px: 2.5,
  backgroundColor: BRAND_ORANGE,
  color: "#fff",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: BRAND_ORANGE_HOVER,
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    backgroundColor: "#e5e7eb",
    color: "#9ca3af",
  },
};

const COLUMN_HEADERS = [
  { label: "Name", field: "resourcename", align: "center", width: "25%" },
  { label: "Description", field: "description", align: "center", width: "25%" },
  { label: "Accounts", field: "accounts", align: "center", width: "25%" },
  { label: "Action", field: null, align: "center", width: "25%" },
];

const isExcludedAccount = (accountNumber) =>
  !accountNumber ||
  String(accountNumber).includes("INIT00000001") ||
  String(accountNumber).includes("TEST");

const accountBelongsToResourceGroup = (account, resourceGroupName) => {
  const groups = account?.accountResourceGroups;
  if (!Array.isArray(groups) || !resourceGroupName) return false;
  return groups.some(
    (rg) => rg === resourceGroupName || rg?.resourcename === resourceGroupName
  );
};

const displayValue = (value) => {
  if (value == null || value === "" || value === "null" || value === "undefined") {
    return "—";
  }
  return String(value);
};

const ResourceGroupsConfig = (props) => {
  const { accounts, organizations } = useSelector((state) => {
    const { accounts, organizations } = state.orgAccount;
    return {
      accounts,
      organizations:
        organizations && organizations.length > 0 ? organizations[0] : {},
    };
  });

  const token = accessToken();
  const decoded = jwtDecode(token);
  const userName = decoded["email"];
  const userRole = getUserRole();

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [resourceGroupName, setResourceGroupName] = useState("");
  const [resourceGroupDescription, setResourceGroupDescription] = useState("");
  const [resourceGroupToDelete, setResourceGroupToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [accountsModalOpen, setAccountsModalOpen] = useState(false);
  const [accountsModalTitle, setAccountsModalTitle] = useState("");
  const [accountsModalList, setAccountsModalList] = useState([]);
  const [deleteResourceGroupError, setDeleteResourceGroupError] =
    useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [orderBy, setOrderBy] = useState("resourcename");
  const [order, setOrder] = useState("asc");

  const getAccountsForResourceGroup = useCallback(
    (resourceGroupName) => {
      if (!Array.isArray(accounts) || !resourceGroupName) return [];
      return accounts.filter(
        (acct) =>
          !isExcludedAccount(acct?.accountNumber) &&
          accountBelongsToResourceGroup(acct, resourceGroupName)
      );
    },
    [accounts]
  );

  const openAccountsModal = (resourceGroupName) => {
    const rgAccounts = getAccountsForResourceGroup(resourceGroupName);
    setAccountsModalTitle(resourceGroupName);
    setAccountsModalList(rgAccounts);
    setAccountsModalOpen(true);
  };

  const closeAccountsModal = () => {
    setAccountsModalOpen(false);
    setAccountsModalTitle("");
    setAccountsModalList([]);
  };

  const clearState = () => {
    setResourceGroupName("");
    setResourceGroupDescription("");
    setDeleteResourceGroupError(null);
  };

  const handleAddDialogClose = () => {
    clearState();
    props.clearAllMessages();
    setAddDialogOpen(false);
  };

  const openDeleteModal = (row) => {
    setResourceGroupToDelete(row.resourcename);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setResourceGroupToDelete(null);
    setDeleteResourceGroupError(null);
    if (props.deleteResourceGroupMessage || props.deleteResourceGroupError) {
      props.clearAllMessages();
    }
  };

  const handleDeleteConfirm = () => {
    if (resourceGroupToDelete) {
      props.deleteResourceGroup(resourceGroupToDelete, organizations?.orgcode);
    }
  };

  const handleAddSubmit = (e) => {
    e.preventDefault();
    const resourceGroup = {
      resourceGroup: resourceGroupName,
      description: resourceGroupDescription,
      orgCode: organizations?.orgcode,
    };
    props.addResourceGroup(resourceGroup);
  };

  const closeDeleteModalCached = useCallback(() => {
    closeDeleteModal();
  }, []);
  const clearAllMessages = useCallback(() => {
    props.clearAllMessages();
  }, []);
  const getResourceGroups = useCallback(() => {
    props.getResourceGroups();
  }, []);
  const getListOfOrgsAndAccts = useCallback(() => {
    props.getListOfOrgsAndAccts();
  }, []);

  const getMsg = props.getResourceGroupsMessage;
  const getErr = props.getResourceGroupsError;
  const deleteMsg = props.deleteResourceGroupMessage;
  const deleteErr = props.deleteResourceGroupError;
  const addMsg = props.addResourceGroupMessage;

  useEffect(() => {
    getListOfOrgsAndAccts();
  }, [getListOfOrgsAndAccts]);

  useEffect(() => {
    getResourceGroups();
  }, [getResourceGroups]);

  useEffect(() => {
    if (addMsg) {
      setAddDialogOpen(false);
      clearState();
      getResourceGroups();
      getListOfOrgsAndAccts();
    }
  }, [addMsg, getResourceGroups, getListOfOrgsAndAccts]);

  useEffect(() => {
    if (deleteMsg) {
      getListOfOrgsAndAccts();
      closeDeleteModalCached();
    }
    if (deleteErr) {
      setDeleteResourceGroupError(deleteErr);
    }
  }, [closeDeleteModalCached, deleteMsg, deleteErr, getListOfOrgsAndAccts]);

  useEffect(() => {
    if (getMsg || getErr) {
      setTimeout(() => {
        clearAllMessages();
      }, 5000);
    }
  }, [clearAllMessages, getMsg, getErr]);

  const handleSort = (field) => {
    if (orderBy === field) {
      setOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setOrderBy(field);
      setOrder("asc");
    }
  };

  const baseRows = useMemo(
    () =>
      (props.resourceGroups || []).filter(
        (row) => row && typeof row === "object" && row.resourcename
      ),
    [props.resourceGroups]
  );

  const filteredRows = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return baseRows;
    return baseRows.filter((row) => {
      const haystack = [row.resourcename, row.description]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [baseRows, searchTerm]);

  const sortedRows = useMemo(() => {
    const sorted = [...filteredRows];
    sorted.sort((a, b) => {
      let aVal;
      let bVal;
      if (orderBy === "accounts") {
        aVal = getAccountsForResourceGroup(a.resourcename).length;
        bVal = getAccountsForResourceGroup(b.resourcename).length;
      } else {
        aVal = (a[orderBy] || "").toString().toLowerCase();
        bVal = (b[orderBy] || "").toString().toLowerCase();
      }
      if (aVal < bVal) return order === "asc" ? -1 : 1;
      if (aVal > bVal) return order === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredRows, orderBy, order, getAccountsForResourceGroup]);

  const pagedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedRows.slice(start, start + rowsPerPage);
  }, [sortedRows, page, rowsPerPage]);

  const canAdd = allowedRoles.includes(userRole) && !props.isAddingResourceGroup;
  const canDelete = allowedRoles.includes(userRole);

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        border: "1px solid #e5e7eb",
        borderRadius: "12px",
        p: { xs: 1.5, sm: 2.5 },
        backgroundColor: "#f3f4f6",
        boxSizing: "border-box",
        ...ACCOUNT_DETAIL_FONT_SX,
      }}
    >
      <Box
        sx={{
          pb: 1.25,
          mb: 1.75,
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            color: "#111827",
            fontSize: "1rem",
          }}
        >
          Resource Groups
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Typography
          sx={{
            color: "#64748b",
            fontSize: "0.875rem",
            fontWeight: 400,
            lineHeight: 1.5,
            flex: 1,
            minWidth: 0,
          }}
        >
          Group related users and cloud accounts as a single entity.
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
            width: { xs: "100%", sm: "auto" },
            flexShrink: 0,
          }}
        >
          <TextField
            size="small"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              flex: { xs: "1 1 160px", sm: "0 0 auto" },
              width: { xs: "auto", sm: 220 },
              minWidth: { xs: 0, sm: 220 },
              "& .MuiOutlinedInput-root": {
                height: 40,
                fontSize: "0.875rem",
                backgroundColor: "white",
                borderRadius: "8px",
                "& fieldset": { borderColor: "#e2e8f0" },
                "&:hover fieldset": { borderColor: "#cbd5e1" },
                "&.Mui-focused fieldset": {
                  borderColor: "#111827",
                  borderWidth: "1px",
                },
              },
              "& .MuiInputBase-input": {
                py: 0,
                height: "100%",
                boxSizing: "border-box",
              },
            }}
          />
          <Button
            onClick={() => setAddDialogOpen(true)}
            disabled={!canAdd}
            startIcon={<i className="mdi mdi-plus" />}
            sx={{
              height: 40,
              minHeight: 40,
              flex: { xs: "1 1 auto", sm: "0 0 auto" },
              backgroundColor: "#F98125",
              color: "#fff",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.875rem",
              borderRadius: "8px",
              px: 2,
              py: 0,
              boxShadow: "none",
              whiteSpace: "nowrap",
              lineHeight: 1,
              "& .MuiButton-startIcon": { mr: 0.75 },
              "&:hover": { backgroundColor: "#e8741c", boxShadow: "none" },
              "&.Mui-disabled": {
                backgroundColor: "#e5e7eb",
                color: "#9ca3af",
              },
            }}
          >
            Add Resource Group
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          border: "1px solid #e2e8f0",
          borderRadius: "12px",
          backgroundColor: "#fff",
          overflow: "hidden",
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
        }}
      >
        {props.isGettingResourceGroups ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 240,
            }}
          >
            <img src={spinner} width="32px" alt="Spinner" />
          </Box>
        ) : (
          <>
            <TableContainer
              sx={{
                width: "100%",
                maxWidth: "100%",
                overflowX: "auto",
                WebkitOverflowScrolling: "touch",
              }}
            >
              <Table
                size="small"
                sx={{
                  width: "100%",
                  minWidth: 640,
                  tableLayout: "fixed",
                }}
              >
                <colgroup>
                  {COLUMN_HEADERS.map((header) => (
                    <col key={header.label} style={{ width: header.width }} />
                  ))}
                </colgroup>
                <TableHead>
                  <TableRow>
                    {COLUMN_HEADERS.map((header) =>
                      header.field ? (
                        <EditAccountSortableHeader
                          key={header.label}
                          field={header.field}
                          orderBy={orderBy}
                          order={order}
                          onSort={handleSort}
                          align={header.align}
                          headerCellSx={HEADER_CELL_SX}
                        >
                          {header.label}
                        </EditAccountSortableHeader>
                      ) : (
                        <TableCell
                          key={header.label}
                          align={header.align}
                          sx={HEADER_CELL_SX}
                        >
                          {header.label}
                        </TableCell>
                      )
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedRows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={COLUMN_HEADERS.length}
                        align="center"
                        sx={{ ...BODY_CELL_SX, py: 6 }}
                      >
                        <Typography sx={{ ...CELL_TEXT, color: "#64748b" }}>
                          No resource groups found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    pagedRows.map((row) => {
                      const accountCount = getAccountsForResourceGroup(
                        row.resourcename
                      ).length;
                      return (
                        <TableRow
                          key={row.resourcename}
                          sx={{ "&:hover": { backgroundColor: "#f8fafc" } }}
                        >
                          <TableCell align="center" sx={BODY_CELL_SX}>
                            <TruncatedTooltipText
                              sx={{ ...CELL_TEXT, textAlign: "center" }}
                            >
                              {displayValue(row.resourcename)}
                            </TruncatedTooltipText>
                          </TableCell>
                          <TableCell align="center" sx={BODY_CELL_SX}>
                            <TruncatedTooltipText
                              sx={{ ...CELL_TEXT, textAlign: "center" }}
                            >
                              {displayValue(row.description)}
                            </TruncatedTooltipText>
                          </TableCell>
                          <TableCell align="center" sx={BODY_CELL_SX}>
                            <Chip
                              label={accountCount}
                              size="small"
                              onClick={
                                accountCount > 0
                                  ? () => openAccountsModal(row.resourcename)
                                  : undefined
                              }
                              sx={{
                                height: 22,
                                minWidth: 28,
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                color: "#334155",
                                backgroundColor: "#f1f5f9",
                                borderRadius: "6px",
                                cursor:
                                  accountCount > 0 ? "pointer" : "default",
                                "& .MuiChip-label": { px: 1 },
                                "&:hover":
                                  accountCount > 0
                                    ? { backgroundColor: "#e2e8f0" }
                                    : undefined,
                              }}
                            />
                          </TableCell>
                          <TableCell align="center" sx={BODY_CELL_SX}>
                            <AccountDetailTooltip title="Delete Resource Group">
                              <span>
                                <IconButton
                                  size="small"
                                  aria-label="Delete resource group"
                                  disabled={!canDelete}
                                  onClick={() => openDeleteModal(row)}
                                  sx={DELETE_BTN_SX}
                                >
                                  <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </span>
                            </AccountDetailTooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <EditAccountTablePagination
              page={page}
              rowsPerPage={rowsPerPage}
              total={sortedRows.length}
              rowsPerPageOptions={[5, 10, 20, 50, 100]}
              onPageChange={setPage}
              onRowsPerPageChange={(value) => {
                setRowsPerPage(value);
                setPage(0);
              }}
            />
          </>
        )}
      </Box>

      {props.getResourceGroupsError && (
        <Snackbar
          open={props.getResourceGroupsError !== null}
          autoHideDuration={5000}
        >
          <SnackbarAlert severity="error">
            {props.getResourceGroupsError}
          </SnackbarAlert>
        </Snackbar>
      )}
      {props.deleteResourceGroupMessage && (
        <Snackbar
          open={props.deleteResourceGroupMessage !== null}
          autoHideDuration={5000}
        >
          <SnackbarAlert severity="success">
            {props.deleteResourceGroupMessage}
          </SnackbarAlert>
        </Snackbar>
      )}
      {props.addResourceGroupMessage && (
        <Snackbar
          open={props.addResourceGroupMessage !== null}
          autoHideDuration={5000}
        >
          <SnackbarAlert severity="success">
            Resource Group Added Successfully!
          </SnackbarAlert>
        </Snackbar>
      )}
      {props.editResourceGroupMessage && (
        <Snackbar
          open={props.editResourceGroupMessage !== null}
          autoHideDuration={5000}
        >
          <SnackbarAlert severity="success">
            {props.editResourceGroupMessage}
          </SnackbarAlert>
        </Snackbar>
      )}

      {/* Modal: accounts linked to a resource group */}
      <ReactstrapModal
        isOpen={accountsModalOpen}
        size="lg"
        centered
        toggle={closeAccountsModal}
      >
        <ModalHeader
          toggle={closeAccountsModal}
          style={{
            backgroundColor: "#F98125",
            margin: -1,
            display: "flex",
            alignItems: "center",
            minHeight: 56,
          }}
          close={
            <button
              type="button"
              aria-label="Close"
              onClick={closeAccountsModal}
              style={CLOSE_X_STYLE}
            >
              <span aria-hidden="true">×</span>
            </button>
          }
        >
          <Typography color="#FFFFFF" variant="h6">
            Accounts — {accountsModalTitle}
          </Typography>
        </ModalHeader>
        <ModalBody
          style={{
            maxHeight: "calc(100vh - 120px)",
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {accountsModalList.length === 0 ? (
            <Typography sx={{ ...CELL_TEXT, color: "#64748b", m: 0 }}>
              No accounts associated with this resource group.
            </Typography>
          ) : (
            <TableContainer
              sx={{
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={HEADER_CELL_SX}>Account Name</TableCell>
                    <TableCell sx={HEADER_CELL_SX}>Account Number</TableCell>
                    <TableCell sx={HEADER_CELL_SX}>Account Type</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {accountsModalList.map((acct) => (
                    <TableRow
                      key={acct.accountNumber || acct.accountCode}
                      sx={{ "&:hover": { backgroundColor: "#f8fafc" } }}
                    >
                      <TableCell sx={BODY_CELL_SX}>
                        {acct.accountName || "—"}
                      </TableCell>
                      <TableCell sx={BODY_CELL_SX}>
                        {acct.accountNumber || "—"}
                      </TableCell>
                      <TableCell sx={BODY_CELL_SX}>
                        {acct.accountType || acct.accounttype || "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <hr
            style={{
              backgroundColor: "rgba(0, 0, 0, 0.1)",
              width: "100%",
              marginTop: 20,
            }}
          />
          <Box
            sx={{
              display: "flex",
              marginTop: "10px",
              gap: "10px",
              justifyContent: "flex-start",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Button
              variant="contained"
              disableElevation
              onClick={closeAccountsModal}
              sx={GREY_CLOSE_BTN_SX}
            >
              Close
            </Button>
          </Box>
        </ModalBody>
      </ReactstrapModal>

      {/* Modal to add a resource group */}
      <ReactstrapModal
        isOpen={addDialogOpen}
        size="lg"
        centered
        toggle={handleAddDialogClose}
      >
        <ModalHeader
          toggle={handleAddDialogClose}
          style={{
            backgroundColor: BRAND_ORANGE,
            margin: -1,
            display: "flex",
            alignItems: "center",
            minHeight: 56,
          }}
          close={
            <button
              type="button"
              aria-label="Close"
              onClick={handleAddDialogClose}
              style={CLOSE_X_STYLE}
            >
              <span aria-hidden="true">×</span>
            </button>
          }
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "1.15rem",
              color: "#FFFFFF",
              letterSpacing: "-0.02em",
              lineHeight: 1.2,
            }}
          >
            Add Resource Group
          </Typography>
        </ModalHeader>
        <ModalBody
          style={{
            maxHeight: "calc(100vh - 120px)",
            overflowY: "auto",
            overflowX: "hidden",
            ...ACCOUNT_DETAIL_FONT_SX,
          }}
        >
          {props.isAddingResourceGroup && (
            <img src={spinner} width="32px" alt="Spinner" />
          )}
          {props.addResourceGroupError && (
            <Alert className="mt-3 mb-0" color="danger">
              Error while adding a resource group.
              {JSON.stringify(props.addResourceGroupError)}
            </Alert>
          )}
          <form onSubmit={handleAddSubmit}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              <MuiTextField
                label="Resource Group Name"
                size="small"
                fullWidth
                variant="outlined"
                value={resourceGroupName}
                onChange={(e) => {
                  setResourceGroupName(e.target.value);
                }}
                InputProps={{
                  style: {
                    fontSize: "0.875rem",
                    borderRadius: 8,
                    backgroundColor: "white",
                  },
                }}
                InputLabelProps={{
                  style: { fontSize: "0.875rem", color: "#64748b" },
                }}
              />
              <MuiTextField
                label="Resource Group Description"
                size="small"
                fullWidth
                variant="outlined"
                value={resourceGroupDescription}
                onChange={(e) => {
                  setResourceGroupDescription(e.target.value);
                }}
                InputProps={{
                  style: {
                    fontSize: "0.875rem",
                    borderRadius: 8,
                    backgroundColor: "white",
                  },
                }}
                InputLabelProps={{
                  style: { fontSize: "0.875rem", color: "#64748b" },
                }}
              />
            </Box>
            <hr
              style={{
                backgroundColor: "rgba(0, 0, 0, 0.1)",
                width: "100%",
                marginTop: 20,
              }}
            />
            <Box
              sx={{
                display: "flex",
                marginTop: "10px",
                gap: 1.25,
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}
            >
              <Button
                type="button"
                variant="contained"
                disableElevation
                onClick={handleAddDialogClose}
                disabled={props.isAddingResourceGroup}
                sx={GREY_CLOSE_BTN_SX}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disableElevation
                disabled={props.isAddingResourceGroup}
                sx={PRIMARY_BTN_SX}
              >
                {props.isAddingResourceGroup
                  ? "Loading ..."
                  : "Add Resource Group"}
              </Button>
            </Box>
          </form>
        </ModalBody>
      </ReactstrapModal>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={showDeleteModal}
        onClose={(_event, reason) => {
          if (reason === "backdropClick" || props.isDeletingResourceGroup) return;
          closeDeleteModal();
        }}
        maxWidth="xs"
        fullWidth
        sx={{ zIndex: 1400 }}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            width: { xs: "calc(100% - 24px)", sm: "100%" },
            m: { xs: 1.5, sm: 2 },
            overflow: "hidden",
            ...ACCOUNT_DETAIL_FONT_SX,
          },
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            backgroundColor: BRAND_ORANGE,
          }}
        >
          <Typography
            id="delete-confirmation-modal"
            sx={{
              fontWeight: 700,
              fontSize: "1.15rem",
              color: "#FFFFFF",
              letterSpacing: "-0.02em",
              lineHeight: 1.2,
            }}
          >
            Confirm Delete
          </Typography>
          <button
            type="button"
            aria-label="Close"
            onClick={closeDeleteModal}
            disabled={!!props.isDeletingResourceGroup}
            style={{
              ...CLOSE_X_STYLE,
              cursor: props.isDeletingResourceGroup ? "not-allowed" : "pointer",
              opacity: props.isDeletingResourceGroup ? 0.6 : 1,
            }}
          >
            <span aria-hidden="true">×</span>
          </button>
        </Box>
        <DialogContent sx={{ px: 3, py: 2.5 }}>
          <Typography
            id="confirm-delete-organization"
            sx={{
              fontSize: "0.9375rem",
              fontWeight: 400,
              color: "#334155",
              lineHeight: 1.5,
              wordBreak: "break-word",
            }}
          >
            Are you sure you want to delete this Resource Group?
          </Typography>
          {deleteResourceGroupError && (
            <Alert className="mt-3 mb-0" color="danger">
              Error while deleting a resource group.
              {JSON.stringify(deleteResourceGroupError)}
            </Alert>
          )}
          <Box
            sx={{
              mt: 3,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1.25,
              width: "100%",
            }}
          >
            <Button
              variant="contained"
              disableElevation
              onClick={closeDeleteModal}
              disabled={!!props.isDeletingResourceGroup}
              sx={GREY_CLOSE_BTN_SX}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disableElevation
              onClick={handleDeleteConfirm}
              disabled={!!props.isDeletingResourceGroup}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 14,
                borderRadius: "8px",
                px: 2.5,
                boxShadow: "none",
                backgroundColor: "#ef4444",
                color: "#fff",
                "&:hover": {
                  backgroundColor: "#dc2626",
                  boxShadow: "none",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#fca5a5",
                  color: "#fff",
                },
              }}
            >
              {props.isDeletingResourceGroup ? "Deleting..." : "Delete"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

const mapStateToProps = (state) => {
  const {
    isGettingResourceGroups,
    getResourceGroupsMessage,
    getResourceGroupsError,

    isAddingResourceGroup,
    addResourceGroupMessage,
    addResourceGroupError,

    isEditingResourceGroup,
    editResourceGroupMessage,
    editResourceGroupError,

    isDeletingResourceGroup,
    deleteResourceGroupError,
    deleteResourceGroupMessage,

    resourceGroups,
  } = state.resourceGroup;
  return {
    isGettingResourceGroups,
    getResourceGroupsMessage,
    getResourceGroupsError,

    isAddingResourceGroup,
    addResourceGroupMessage,
    addResourceGroupError,

    isEditingResourceGroup,
    editResourceGroupMessage,
    editResourceGroupError,

    isDeletingResourceGroup,
    deleteResourceGroupError,
    deleteResourceGroupMessage,

    resourceGroups,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getResourceGroups: () => {
      dispatch(getResourceGroups());
    },
    addResourceGroup: (resourceGroup) => {
      dispatch(addResourceGroup(resourceGroup));
    },
    editResourceGroup: (resourceGroup) => {
      dispatch(editResourceGroup(resourceGroup));
    },
    deleteResourceGroup: (resourceGroupName, orgCode) => {
      dispatch(deleteResourceGroup(resourceGroupName, orgCode));
    },
    clearAllMessages: () => {
      dispatch(clearAllResourceGroupMessages());
    },
    getListOfOrgsAndAccts: () => {
      dispatch(getListOfOrgsAndAccts());
    },
  };
};
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ResourceGroupsConfig);
